// Onboarding tour for the landing page.
// First-time visitors see a "Want a tour?" prompt; accepting walks them
// through a spotlight of the hero arrow, nav links, and the main CTA.
// Returning visitors get a persistent "Take a tour" pill so they can replay it.

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";

// Brand palette — kept in sync with the landing hero / navigation.
const fontMono = "'JetBrains Mono', 'Fira Mono', 'Menlo', monospace";
const yellow = "#D9FF5B";
const green = "#04c389";
const dark = "#1D1D1D";

// localStorage flag so the tour auto-opens only once per browser.
// Clear this key in DevTools to re-test the first-visit flow.
const STORAGE_KEY = "foodoscope_tour_seen";

// Ordered tour steps. Each `id` must match a `data-tour="<id>"` attribute
// on the rendered DOM (see HeroSection.js and Navigation.js).
// `placement` controls which side of the target the tooltip floats on.
const DESKTOP_STEPS = [
  {
    id: "hero-arrow",
    title: "Welcome to Foodoscope",
    placement: "right",
  },
  {
    id: "nav-auth",
    title: "Here you can Login / SignUp",
    placement: "bottom-left",
  },
  {
    id: "nav-apis",
    title: "Browse our APIs from here",
    placement: "bottom",
  },
  {
    id: "nav-faqs",
    title: "Find quick answers in FAQs",
    placement: "bottom",
  },
  {
    id: "hero-cta",
    title: "Ready? Start exploring the APIs",
    placement: "right",
  },
];

// Mobile collapses Login / APIs / FAQs into the hamburger menu, so those
// individual targets aren't in the DOM. Instead we point at the hamburger
// itself with a single combined message. Tooltip placement is ignored on
// mobile (always docks to the bottom), so any value here is fine.
const MOBILE_STEPS = [
  {
    id: "hero-arrow",
    title: "Welcome to Foodoscope",
    placement: "bottom",
  },
  {
    id: "nav-menu",
    title: "Tap the menu for Login, APIs and FAQs",
    placement: "bottom",
  },
  {
    id: "hero-cta",
    title: "Ready? Start exploring the APIs",
    placement: "bottom",
  },
];

// Floating tooltip rendered next to the spotlighted target.
// On the final step it detaches from any target and shows a personalised greeting.
function Tooltip({ rect, step, greetingName, isFinal, onNext, onSkip, totalSteps, currentIndex, isMobile }) {
  // No rect yet (target hasn't mounted) and not the final greeting — render nothing.
  if (!rect && !isFinal) return null;

  // Mobile: pin the tooltip to the top or bottom of the viewport depending on
  // where the spotlight sits, so the tooltip never overlaps the highlighted
  // element. Tooltip stays inside the viewport regardless of `placement`.
  // Desktop: position relative to target via `placement`.
  let style;
  if (isMobile) {
    const vh = typeof window !== "undefined" ? window.innerHeight : 800;
    // If the spotlight is in the lower half of the viewport, put the tooltip
    // at the top; otherwise at the bottom. Final step always sits at the bottom.
    const spotlightCenterY = rect ? rect.top + rect.height / 2 : vh / 2;
    const dockTop = !isFinal && spotlightCenterY > vh / 2;
    style = dockTop
      ? { left: 12, right: 12, top: 16 }
      : { left: 12, right: 12, bottom: 16 };
  } else if (isFinal) {
    // Final step is untethered — anchor it bottom-left of the viewport.
    style = { left: 80, top: window.innerHeight - 180, maxWidth: 360 };
  } else {
    style = { ...computeTooltipPosition(rect, step.placement), maxWidth: 360 };
  }

  return (
    <div
      className="fixed z-[10001] pointer-events-auto"
      style={{
        ...style,
        fontFamily: fontMono,
      }}
    >
      <div className="flex items-start gap-2">
        {/* Yellow dot — visual anchor between target and tooltip */}
        <span
          className="inline-block mt-2 flex-shrink-0 rounded-full"
          style={{ width: 14, height: 14, background: yellow }}
        />
        <div
          className="rounded-md px-3 py-2 text-white text-sm sm:text-base shadow-xl flex-1 min-w-0"
          style={{
            background: dark,
            border: "1px solid #3a3a3a",
            fontFamily: fontMono,
          }}
        >
          {isFinal ? (
            <>
              <div>Hi, {greetingName || "there"}</div>
              <div>Welcome to Foodoscope</div>
            </>
          ) : (
            <div>{step.title}</div>
          )}
        </div>
      </div>

      {/* Controls row: progress counter + Next / Skip buttons */}
      <div className="flex items-center gap-2 mt-3 ml-6">
        {!isFinal && (
          <span className="text-xs text-white/60" style={{ fontFamily: fontMono }}>
            {currentIndex + 1} / {totalSteps}
          </span>
        )}
        <button
          onClick={onNext}
          className="rounded-full px-3 py-1 text-xs font-semibold"
          style={{ background: yellow, color: dark, fontFamily: fontMono }}
        >
          {isFinal ? "Done" : currentIndex === totalSteps - 1 ? "Finish" : "Next"}
        </button>
        {!isFinal && (
          <button
            onClick={onSkip}
            className="rounded-full px-3 py-1 text-xs font-semibold border"
            style={{
              borderColor: "#ffffff55",
              color: "#fff",
              fontFamily: fontMono,
              background: "transparent",
            }}
          >
            Skip
          </button>
        )}
      </div>
    </div>
  );
}

// Picks a viewport-safe top/left for the tooltip given the target rect and
// preferred placement. Result is clamped so the tooltip never sits off-screen
// on small viewports (which would otherwise happen for nav items near edges).
function computeTooltipPosition(rect, placement) {
  const margin = 16;
  // Approximate tooltip footprint — used only for viewport clamping below.
  const tooltipW = 380;
  const tooltipH = 110;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let top, left;
  switch (placement) {
    case "right":
      top = rect.top;
      left = rect.right + margin;
      break;
    case "left":
      top = rect.top;
      left = rect.left - tooltipW - margin;
      break;
    case "bottom":
      top = rect.bottom + margin;
      left = rect.left;
      break;
    case "bottom-left":
      // Align the tooltip's right edge with the target's right edge
      // so it doesn't run off-screen for nav items near the right gutter.
      top = rect.bottom + margin;
      left = Math.max(margin, rect.right - tooltipW);
      break;
    case "top":
    default:
      top = rect.top - tooltipH - margin;
      left = rect.left;
      break;
  }

  // Clamp inside the viewport so edge-placed targets stay visible.
  left = Math.min(Math.max(margin, left), vw - tooltipW - margin);
  top = Math.min(Math.max(margin, top), vh - tooltipH - margin);
  return { top, left };
}

// First-visit "Want a tour?" prompt shown in the top-right corner.
// Lives at the same coordinates as the "Take a tour" pill so they swap in place.
// On mobile the pill shrinks (smaller padding, smaller text) so it doesn't crowd the nav.
function PromptCard({ onAccept, onDecline }) {
  return (
    <div
      className="fixed right-3 sm:right-6 z-[10001] flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-2 sm:py-3 rounded-full shadow-2xl"
      style={{
        top: 100,
        background: dark,
        border: `2px solid ${yellow}`,
        fontFamily: fontMono,
        boxShadow: `0 0 0 4px rgba(217,255,91,0.15), 0 10px 30px rgba(0,0,0,0.5)`,
      }}
    >
      <span
        className="inline-block rounded-full animate-pulse"
        style={{ width: 12, height: 12, background: yellow }}
      />
      <span className="text-white text-xs sm:text-base font-semibold whitespace-nowrap">
        <span className="hidden sm:inline">Want a tour?</span>
        <span className="sm:hidden">Tour?</span>
      </span>
      <button
        onClick={onAccept}
        className="rounded-full px-2.5 sm:px-4 py-0.5 sm:py-1.5 text-xs sm:text-sm font-semibold"
        style={{ background: yellow, color: dark }}
      >
        <span className="hidden sm:inline">Sure</span>
        <span className="sm:hidden">Yes</span>
      </button>
      <button
        onClick={onDecline}
        className="rounded-full px-2.5 sm:px-4 py-0.5 sm:py-1.5 text-xs sm:text-sm font-semibold border"
        style={{ borderColor: "#ffffff55", color: "#fff", background: "transparent" }}
      >
        <span className="hidden sm:inline">Later</span>
        <span className="sm:hidden">No</span>
      </button>
    </div>
  );
}

// Breakpoint below which the tour switches to its mobile layout (px).
// Matches Tailwind's `sm` breakpoint so styles stay in step with the rest of the app.
const MOBILE_BREAKPOINT = 640;

export default function OnboardingTour() {
  // Personalises the final greeting when the user is logged in.
  // `useAuth()` may be undefined during early renders — fall back to {}.
  const { profile } = useAuth() || {};
  const greetingName = profile?.firstname || profile?.name;

  // Phase machine:
  //   idle    — initial; deciding whether to ask
  //   asking  — "Want a tour?" prompt visible
  //   running — walking through STEPS with spotlight + tooltip
  //   final   — last untethered greeting card
  //   done    — tour finished/skipped; "Take a tour" pill shown for replay
  const [phase, setPhase] = useState("idle");
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState(null);
  // Tracks viewport width so we can switch layout when the user rotates / resizes.
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT
  );
  // requestAnimationFrame handle for the live rect tracker (see below).
  const rafRef = useRef(0);

  // Mobile collapses nav into a hamburger, so we use a shorter step list there.
  const steps = isMobile ? MOBILE_STEPS : DESKTOP_STEPS;

  // Keep `isMobile` in sync with viewport changes (e.g. orientation flip).
  // Also reset to step 0 on a layout switch — the two step lists have different
  // lengths/targets, so reusing the old index would point at the wrong thing.
  useEffect(() => {
    const onResize = () => {
      setIsMobile((prev) => {
        const next = window.innerWidth < MOBILE_BREAKPOINT;
        if (next !== prev) setStepIndex(0);
        return next;
      });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // First-mount: decide whether to auto-open the tour.
  // Small delay so the prompt doesn't pop before the hero finishes animating in.
  useEffect(() => {
    try {
      const seen = localStorage.getItem(STORAGE_KEY);
      if (!seen) {
        const t = setTimeout(() => setPhase("asking"), 800);
        return () => clearTimeout(t);
      }
      // Returning visitor — skip straight to the replay pill.
      setPhase("done");
    } catch (e) {
      // localStorage can throw in private mode / strict cookie settings.
      // Treat as "never seen" so the user still gets the option.
      setPhase("asking");
    }
  }, []);

  // Keep the spotlight glued to the current target while the tour is running.
  // We re-measure on every animation frame because the page can shift due to
  // image loads, font swaps, or sticky-nav transitions that resize/scroll alone
  // wouldn't catch. Cost is negligible compared to the perceived alignment win.
  useLayoutEffect(() => {
    if (phase !== "running") return;
    const step = steps[stepIndex];
    if (!step) return;

    const measure = () => {
      const el = document.querySelector(`[data-tour="${step.id}"]`);
      if (el) {
        const r = el.getBoundingClientRect();
        // Copy the rect — DOMRect is a live-ish object and we want a stable snapshot.
        setRect({
          top: r.top,
          left: r.left,
          right: r.right,
          bottom: r.bottom,
          width: r.width,
          height: r.height,
        });
      } else {
        // Target not in the DOM yet (or removed) — hide spotlight until it returns.
        setRect(null);
      }
    };

    const loop = () => {
      measure();
      rafRef.current = requestAnimationFrame(loop);
    };
    loop();

    // Resize and scroll are kept as safety nets in case rAF is throttled
    // (e.g. background tab) and the target moves while we're paused.
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [phase, stepIndex, isMobile, steps]);

  // When the tour starts or advances, scroll the target into view so the
  // spotlight isn't sitting off-screen.
  useEffect(() => {
    if (phase !== "running") return;
    const step = steps[stepIndex];
    if (!step) return;
    const el = document.querySelector(`[data-tour="${step.id}"]`);
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
    }
  }, [phase, stepIndex, steps]);

  // Prevent user scrolling (wheel, touch, keyboard) during active tour so the
  // spotlight stays aligned with the target element without visual artifacts.
  useEffect(() => {
    if (phase !== "running") return;

    const preventScroll = (e) => {
      e.preventDefault();
    };

    const preventKeyScroll = (e) => {
      if (["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", " "].includes(e.key)) {
        if (!["INPUT", "TEXTAREA"].includes(e.target?.tagName)) {
          e.preventDefault();
        }
      }
    };

    window.addEventListener("wheel", preventScroll, { passive: false });
    window.addEventListener("touchmove", preventScroll, { passive: false });
    window.addEventListener("keydown", preventKeyScroll);

    return () => {
      window.removeEventListener("wheel", preventScroll);
      window.removeEventListener("touchmove", preventScroll);
      window.removeEventListener("keydown", preventKeyScroll);
    };
  }, [phase]);

  // Mark the tour as seen so it doesn't auto-open again on next visit.
  const finish = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch (e) {
      // Storage blocked — pill will keep showing every visit, which is fine.
    }
    setPhase("done");
  };

  const handleAccept = () => {
    setStepIndex(0);
    setPhase("running");
  };

  // "Later" also persists the seen flag — a deliberate dismissal shouldn't nag.
  const handleDecline = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch (e) {}
    setPhase("done");
  };

  const handleNext = () => {
    if (stepIndex < steps.length - 1) {
      setStepIndex(stepIndex + 1);
    } else {
      // Past the last targeted step — show the untethered greeting card.
      setPhase("final");
    }
  };

  // Replay affordance for returning visitors. Clicking it re-enters "asking".
  // Shrinks below the `sm` breakpoint so it doesn't fight the mobile nav for space.
  if (phase === "done") {
    return (
      <button
        onClick={() => setPhase("asking")}
        className="fixed right-3 sm:right-6 z-[10001] flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-2 sm:py-3 rounded-full shadow-2xl hover:scale-105 transition-transform"
        style={{
          top: 100,
          background: dark,
          border: `2px solid ${yellow}`,
          fontFamily: fontMono,
          boxShadow: `0 0 0 4px rgba(217,255,91,0.15), 0 10px 30px rgba(0,0,0,0.5)`,
        }}
        aria-label="Take a tour"
      >
        <span
          className="inline-block rounded-full animate-pulse"
          style={{ width: 12, height: 12, background: yellow }}
        />
        <span className="text-white text-xs sm:text-base font-semibold whitespace-nowrap">
          <span className="hidden sm:inline">Take a tour</span>
          <span className="sm:hidden">Tour</span>
        </span>
      </button>
    );
  }

  if (phase === "asking") {
    return <PromptCard onAccept={handleAccept} onDecline={handleDecline} />;
  }

  const isFinal = phase === "final";
  const step = steps[stepIndex];

  // Spotlight geometry — full-screen dim with a transparent rounded rect
  // punched out via an SVG mask. `pad` adds breathing room around the target.
  const pad = 8;
  const radius = 12;
  let hole = null;
  if (!isFinal && rect) {
    const rawTop = rect.top - pad;
    const rawLeft = rect.left - pad;
    const rawRight = rect.right + pad;
    const rawBottom = rect.bottom + pad;

    const vw = typeof window !== "undefined" ? window.innerWidth : 1000;
    const vh = typeof window !== "undefined" ? window.innerHeight : 1000;

    const clampedTop = Math.max(0, Math.min(rawTop, vh));
    const clampedLeft = Math.max(0, Math.min(rawLeft, vw));
    const clampedRight = Math.max(0, Math.min(rawRight, vw));
    const clampedBottom = Math.max(0, Math.min(rawBottom, vh));

    const w = clampedRight - clampedLeft;
    const h = clampedBottom - clampedTop;

    if (w > 0 && h > 0) {
      hole = { x: clampedLeft, y: clampedTop, w, h };
    }
  }

  return (
    // Outer layer is pointer-events:none so the page stays interactive where
    // we don't explicitly opt in (the SVG dim and the Tooltip do opt in).
    <div className="fixed inset-0 z-[10000] pointer-events-none">
      {/* Dim overlay + spotlight cutout via SVG mask */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-auto"
        style={{ display: "block" }}
      >
        <defs>
          {/* White = visible dim, black = cutout. The black rect punches the hole. */}
          <mask id="foodo-tour-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {hole && (
              <rect
                x={hole.x}
                y={hole.y}
                width={hole.w}
                height={hole.h}
                rx={radius}
                ry={radius}
                fill="black"
              />
            )}
          </mask>
        </defs>
        {/* The dim layer. Clicking it advances the tour (or finishes on the final step). */}
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.65)"
          mask="url(#foodo-tour-mask)"
          onClick={isFinal ? finish : handleNext}
        />
        {/* Green outline around the spotlight for emphasis. pointerEvents:none so
            it never blocks clicks on the underlying target element. */}
        {hole && (
          <rect
            x={hole.x}
            y={hole.y}
            width={hole.w}
            height={hole.h}
            rx={radius}
            ry={radius}
            fill="none"
            stroke={green}
            strokeWidth="2"
            pointerEvents="none"
          />
        )}
      </svg>

      <Tooltip
        rect={rect}
        step={step}
        greetingName={greetingName}
        isFinal={isFinal}
        onNext={isFinal ? finish : handleNext}
        onSkip={handleDecline}
        totalSteps={steps.length}
        currentIndex={stepIndex}
        isMobile={isMobile}
      />
    </div>
  );
}
